import damp from '@studiometa/js-toolkit/utils/math/damp';

/**
 * Handler for the click event on anchor links
 *
 * @param {string} selector The target selector.
 * @param {Object} options Options for the scroll.
 * @param {Object} options.offset Offset to add to the target scroll position.
 * @return {Promise<number>} A promising resolving with the target scroll position.
 */
export default function scrollTo(selector, options = { offset: 0 }) {
  const targetElement = document.querySelector(selector);

  if (!targetElement) {
    return Promise.resolve(window.pageYOffset);
  }

  const sizes = targetElement.getBoundingClientRect();
  const max = document.body.offsetHeight - window.innerHeight;
  let scrollTarget = sizes.top + window.pageYOffset;

  if (typeof options.offset === 'number') {
    scrollTarget += options.offset;
  }

  // Make sure to not scroll more than the max scroll allowed
  if (scrollTarget > max) {
    scrollTarget = max;
  }

  return new Promise((resolve) => {
    let isScrolling = false;
    let scroll = window.pageYOffset;
    let dampScroll = window.pageYOffset;

    /**
     * Handler for the wheel event
     * Is used to cancel the programmatic scroll animation
     */
    function eventHandler() {
      if (isScrolling) {
        isScrolling = false;
      }
    }

    /**
     * Scroll animation end's hook
     */
    function end() {
      window.removeEventListener('wheel', eventHandler);
      window.removeEventListener('touchmove', eventHandler);
      resolve(window.pageYOffset);
    }

    /**
     * Scroll animation's loop
     */
    function loop() {
      if (!isScrolling) {
        return end();
      }

      dampScroll = damp(scroll, dampScroll, 0.2);
      window.scrollTo({ top: dampScroll });

      if (dampScroll === scroll) {
        isScrolling = false;
      }

      return requestAnimationFrame(loop);
    }

    /**
     * Start the scroll animation
     * @param {Number} target The target scroll
     */
    function start(target) {
      // Update vars
      isScrolling = true;
      dampScroll = window.pageYOffset;
      scroll = target;

      // Bind wheel event to stop the animation if
      // the user wants to scroll manually
      window.addEventListener('wheel', eventHandler, { passive: true });
      window.addEventListener('touchmove', eventHandler, { passive: true });

      // Init the loop
      loop();
    }

    // Start the scroll
    start(scrollTarget);
  });
}
